import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRoutedNavigation } from "presentation/hooks/useRoutedNavigation";
import {
  Container,
  Box,
  Typography,
  Alert,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Paper,
  Button,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CategoryIcon from '@mui/icons-material/Category';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import ShareIcon from '@mui/icons-material/Share';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageContainer from '../components/shared/pageContainer';
import PageHeader from '../components/shared/page-header';
import { DetailSkeleton } from '../components/shared/skeletons';
import { useNewsDetailController } from './context/news-detail-controller';
import { useNewsDetailState } from './context/news-detail-context';

export function NewsDetailComponent() {
  const { id } = useParams<{ id: string }>();
  const { navigateTo: navigate } = useRoutedNavigation();
  const { loadDetail } = useNewsDetailController();
  const { detail, loading, error } = useNewsDetailState();

  useEffect(() => {
    if (id) {
      loadDetail(parseInt(id));
    }
  }, [id]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = detail?.title || 'Notícia';

  const handleShareFacebook = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
  };

  const handleShareTwitter = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=400');
  };

  const handleShareInstagram = () => {
    // Instagram não suporta compartilhamento direto via URL
    // Copiar o link para área de transferência
    navigator.clipboard.writeText(shareUrl);
    alert('Link copiado! Cole no Instagram para compartilhar.');
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        });
      } catch (error) {
        console.log('Erro ao compartilhar:', error);
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert('Link copiado para a área de transferência!');
    }
  };

  if (loading) {
    return <DetailSkeleton hasImage={true} hasSections={1} />;
  }

  if (error) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <PageHeader
            title="Notícia"
            subtitle="Detalhes da notícia"
            breadcrumbs={[
              { label: 'Notícias', href: '/noticias' },
              { label: 'Detalhes' },
            ]}
          />
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        </Container>
      </PageContainer>
    );
  }

  if (!detail) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <PageHeader
            title="Notícia"
            subtitle="Detalhes da notícia"
            breadcrumbs={[
              { label: 'Notícias', href: '/noticias' },
              { label: 'Detalhes' },
            ]}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            Notícia não encontrada.
          </Alert>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <PageHeader
          title={detail.title || 'Notícia'}
          subtitle="Detalhes da notícia"
          breadcrumbs={[
            { label: 'Notícias', href: '/noticias' },
            { label: detail.title || 'Detalhes' },
          ]}
        />

        <Paper
          elevation={3}
          sx={{
            p: 4,
            mt: 2,
            borderRadius: 2,
          }}
        >
          {/* Cabeçalho com categoria, data e compartilhamento */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              {detail.category && (
                <Chip
                  icon={<CategoryIcon />}
                  label={detail.category}
                  color="primary"
                  variant="outlined"
                />
              )}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'text.secondary',
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2">
                  {formatDate(detail.publishedDate || detail.date)}
                </Typography>
              </Box>
            </Box>

            {/* Botões de compartilhamento */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Compartilhar:
              </Typography>
              <Tooltip title="Compartilhar no Facebook" arrow>
                <IconButton
                  size="small"
                  onClick={handleShareFacebook}
                  sx={{
                    color: '#1877F2',
                    '&:hover': {
                      backgroundColor: 'rgba(24, 119, 242, 0.1)',
                    },
                  }}
                >
                  <FacebookIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Compartilhar no Twitter" arrow>
                <IconButton
                  size="small"
                  onClick={handleShareTwitter}
                  sx={{
                    color: '#1DA1F2',
                    '&:hover': {
                      backgroundColor: 'rgba(29, 161, 242, 0.1)',
                    },
                  }}
                >
                  <TwitterIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Copiar link para Instagram" arrow>
                <IconButton
                  size="small"
                  onClick={handleShareInstagram}
                  sx={{
                    color: '#E4405F',
                    '&:hover': {
                      backgroundColor: 'rgba(228, 64, 95, 0.1)',
                    },
                  }}
                >
                  <InstagramIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Compartilhar" arrow>
                <IconButton
                  size="small"
                  onClick={handleShare}
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                    },
                  }}
                >
                  <ShareIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Imagem de destaque */}
          {detail.urlImageEmphasis && (
            <Box
              sx={{
                width: '100%',
                mb: 4,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: 2,
              }}
            >
              <img
                src={detail.urlImageEmphasis}
                alt={detail.title || 'Imagem da notícia'}
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '500px',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            </Box>
          )}

          {/* Resumo */}
          {detail.resume && (
            <Box
              sx={{
                p: 3,
                mb: 4,
                backgroundColor: 'grey.100',
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                borderRadius: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: 'primary.main',
                }}
              >
                Resumo
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {detail.resume}
              </Typography>
            </Box>
          )}

          {/* Conteúdo da notícia */}
          {detail.text && (
            <Box
              sx={{
                '& p': {
                  mb: 2,
                  lineHeight: 1.8,
                  fontSize: '1.1rem',
                  color: 'text.primary',
                },
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: 1,
                  my: 2,
                },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  mt: 3,
                  mb: 2,
                  fontWeight: 600,
                  color: 'primary.main',
                },
                '& ul, & ol': {
                  ml: 3,
                  mb: 2,
                },
                '& li': {
                  mb: 1,
                  lineHeight: 1.8,
                },
              }}
              dangerouslySetInnerHTML={{ __html: detail.text }}
            />
          )}
        </Paper>

        {/* Botão de voltar - centralizado no final */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4,
            mb: 3,
          }}
        >
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/noticias')}
            size="large"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5,
            }}
          >
            Voltar para Notícias
          </Button>
        </Box>
      </Container>
    </PageContainer>
  );
}
